import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@ttab/elephant-ui'
import { useRef, useState, useEffect, useCallback } from 'react'
import { useWorkflow } from '@/hooks/index/useWorkflow'
import { getStatusSpecifications, getWorkflowSpecifications, type WorkflowTransition } from '@/defaults/workflowSpecification'
import { useWorkflowStatus } from '@/hooks/useWorkflowStatus'
import { StatusOptions } from './StatusOptions'
import { StatusMenuContext } from './StatusMenuContext'
import { PromptDefault } from './PromptDefault'
import { PromptSchedule } from './PromptSchedule'
import { StatusMenuOption } from './StatusMenuOption'
import { StatusButton } from './StatusButton'
import { useSession } from 'next-auth/react'
import { useRegistry } from '@/hooks/useRegistry'
import { toast } from 'sonner'
import { handleLink } from '../Link/lib/handleLink'
import { useHistory, useNavigation, useView } from '@/hooks/index'
import type { View } from '@/types/index'
import type { YDocument } from '@/modules/yjs/hooks'
import type * as Y from 'yjs'
import { useTranslation } from 'react-i18next'
import { documentTypeValueFormat } from '@/defaults/documentTypeFormats'


export const StatusMenu = ({ ydoc, onBeforeStatusChange, planningId, embargoUntil }: {
  ydoc: YDocument<Y.Map<unknown>>
  onBeforeStatusChange?: (
    status: string,
    data?: Record<string, unknown>
  ) => Promise<boolean>
  planningId?: string
  embargoUntil?: string
}) => {
  const [documentStatus, setDocumentStatus] = useWorkflowStatus({ ydoc })
  const containerRef = useRef<HTMLDivElement>(null)
  const [dropdownWidth, setDropdownWidth] = useState<number>(0)
  const { statuses, workflow } = useWorkflow(documentStatus?.type)
  const [prompt, showPrompt] = useState<{ status: string } & WorkflowTransition | undefined>()
  const { data: session } = useSession()
  const { repository } = useRegistry()
  const { state, dispatch } = useNavigation()
  const history = useHistory()
  const { viewId } = useView()
  const { t } = useTranslation()
  const viewElementRef = useRef<HTMLElement | null>(null)
  const icon = documentTypeValueFormat[(documentStatus?.type || 'core/article') as keyof typeof documentTypeValueFormat].icon

  useEffect(() => {
    viewElementRef.current = document.getElementById(viewId)
  }, [viewId])


  // Read workflow specifications from current type and current status
  const isWorkflow = documentStatus?.type
    ? getWorkflowSpecifications()[documentStatus.type][documentStatus.name].isWorkflow
    : false
  const isChanged = !isWorkflow ? ydoc.isChanged : false
  const asSave = (documentStatus?.type
    ? getWorkflowSpecifications()[documentStatus.type][documentStatus.name].asSave && ydoc.isChanged
    : false) || false
  const requireCause = !!documentStatus?.checkpoint && documentStatus.type
    ? getWorkflowSpecifications()[documentStatus.type][documentStatus.name].requireCause || false
    : false


  useEffect(() => {
    if (containerRef.current) {
      setDropdownWidth(containerRef.current.offsetWidth)
    }
  }, [])

  const [isTransitioning, setIsTransitioning] = useState(false)

  // Callback function to set status. Will first call onBeforeStatusChange() if
  // provided by props, then proceed to change the status if allowed.
  const setStatus = useCallback(async (newStatus: string, data?: Record<string, unknown>) => {
    if (newStatus === 'withheld' && !planningId) {
      toast.error(t('shared:status_menu.schedulingRequiresPlanning'))
      return
    }

    setIsTransitioning(true)

    try {
      if (onBeforeStatusChange) {
        if (await onBeforeStatusChange(newStatus, data) !== true) {
          setIsTransitioning(false)
          return
        }
      }

      await setDocumentStatus(
        newStatus,
        (typeof data?.cause === 'string') ? data.cause : undefined
      )
    } catch (error) {
      console.error('Failed to change status:', error)
    } finally {
      setIsTransitioning(false)
    }
  }, [onBeforeStatusChange, setDocumentStatus, planningId, t])

  const unPublishDocument = async (newStatus?: string) => {
    if (!repository || !session?.accessToken || newStatus !== 'unpublished') {
      return
    }

    // Unpublishing a document is done by setting version: -1, status name to 'usable'
    const status = {
      uuid: ydoc.id,
      name: 'usable',
      version: -1n
    }

    setIsTransitioning(true)

    try {
      await repository.saveMeta({
        status,
        accessToken: session.accessToken,
        cause: documentStatus?.cause,
        isWorkflow: documentStatus?.type === 'core/article',
        currentStatus: documentStatus
      })

      const viewType: Record<string, View> = {
        'core/article': 'Editor',
        'core/planning-item': 'Planning',
        'core/event': 'Event',
        'core/factbox': 'Factbox',
        'core/flash': 'Flash',
        'tt/print-article': 'PrintEditor'
      }
      handleLink({
        dispatch,
        viewItem: state.viewRegistry.get(viewType[documentStatus?.type || 'Error']),
        props: { id: ydoc.id },
        viewId: crypto.randomUUID(),
        history,
        origin: viewId,
        target: 'self'
      })
    } catch (error) {
      toast.error(t('errors:toasts.unpublishError'))
      console.error('error while unpublishing document:', error)
      setIsTransitioning(false)
    }
  }

  if (!documentStatus || !Object.keys(statuses).length) {
    return null
  }

  const currentStatusName = documentStatus.name
  const currentStatusDef = statuses[currentStatusName] || getStatusSpecifications(currentStatusName, documentStatus.type)
  const transitions = workflow[currentStatusName]?.transitions || {}

  if (!Object.keys(transitions).length && currentStatusName !== 'unpublished') {
    return null
  }

  return (
    <>
      <div className='flex items-center' ref={containerRef}>

        <DropdownMenu>
          <DropdownMenuTrigger
            render={(
              <StatusButton
                documentStatus={documentStatus}
                workflow={workflow}
                currentStatusName={currentStatusName}
                currentStatusDef={currentStatusDef}
                asSave={asSave}
                isTransitioning={isTransitioning}
              />
            )}
          />

          <DropdownMenuContent
            className='max-w-[340px] p-0 mt-1'
            align='end'
            sideOffset={0}
            style={{ minWidth: `${dropdownWidth}px` }}
          >
            <StatusOptions
              transitions={transitions}
              statuses={statuses}
              onSelect={showPrompt}
              hasChanges={asSave && isChanged}
              documentType={documentStatus.type}
              disabledTransitions={!planningId
                ? {
                    withheld: {
                      reason: t('shared:status_menu.schedulingRequiresPlanning')
                    }
                  }
                : undefined}
            >
              {asSave && isChanged && (
                <StatusMenuOption
                  key='save'
                  status={documentStatus.name}
                  hasChanges
                  state={{
                    verify: false,
                    isWorkflow: false,
                    title: workflow[currentStatusName]?.asSaveTitle || t('shared:status_menu.asSavePlaceholder'),
                    description: workflow[currentStatusName]?.updateDescription || workflow[currentStatusName]?.description
                  }}
                  onSelect={currentStatusName === 'usable' ? showPrompt : () => setStatus('usable')}
                  statusDef={currentStatusDef}
                />
              )}
            </StatusOptions>

            <StatusMenuContext
              icon={currentStatusDef?.icon || getStatusSpecifications(currentStatusName, documentStatus.type)?.icon}
              className={currentStatusDef?.className || getStatusSpecifications(currentStatusName, documentStatus.type)?.className}
              title={workflow[currentStatusName]?.title}
              description={asSave && isChanged && workflow[currentStatusName]?.changedDescription
                ? workflow[currentStatusName]?.changedDescription
                : workflow[currentStatusName]?.description}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {prompt && (
        <>
          {prompt.status === 'withheld' && planningId && (
            <PromptSchedule
              prompt={prompt}
              showPrompt={showPrompt}
              setStatus={(...args) => void setStatus(...args)}
              planningId={planningId}
              requireCause={!!documentStatus.checkpoint}
              embargoUntil={embargoUntil}
              ydoc={ydoc}
              usableId={documentStatus.usableId}
              documentType={documentStatus.type}
              anchor={viewElementRef.current}
              typeIcon={icon}
            />
          )}

          {prompt.status !== 'withheld' && (
            <PromptDefault
              prompt={prompt}
              showPrompt={showPrompt}
              setStatus={(...args) => void setStatus(...args)}
              currentCause={documentStatus.cause}
              requireCause={requireCause}
              unPublishDocument={unPublishDocument}
              ydoc={ydoc}
              usableId={documentStatus.usableId}
              documentType={documentStatus.type}
              embargoUntil={embargoUntil}
              anchor={viewElementRef.current}
              typeIcon={icon}
            />
          )}
        </>
      )}
    </>
  )
}
